import React, { useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, X, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import OptimizedImage from '@/components/OptimizedImage';
import ListingTypeBadge from '@/components/ListingTypeBadge';
import { formatListingPrice, formatServiceListingPrice } from '@/utils/currencyConverter';
import { formatJobSalary } from '@/utils/formatJobSalary';
import { useCurrency } from '@/contexts/CurrencyContext';
import heroPlaceholder from '@/assets/hero-helicopter-new.jpg';
import { getJobTypeMapPin, isJobOffering } from '@/utils/jobTypeIcons';
import { getServiceTypeMapPin, getServiceCategoryLabel, getServiceCategoryShortLabel } from '@/utils/serviceTypeIcons';
import { useEffectiveVisibility } from '@/hooks/useEffectiveVisibility';
import { BlurredPrivateData } from '@/components/privacy/BlurredPrivateData';
import { useAuth } from '@/components/AuthProvider';
import { useUnifiedContact } from '@/contexts/UnifiedContactContext';

interface MobileMapPreviewCardProps {
  listing: {
    id: string;
    title: string;
    location?: string;
    price?: number;
    listingType?: string;
    heroImage?: string;
    manufacturer?: string;
    model?: string;
    assetType?: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
    companyLogo?: string;
    companyName?: string;
    serviceCategory?: string;
    // Services-specific
    pricingModel?: string;
    baseRate?: number;
    rateUnit?: string;
    // Jobs-specific
    salaryMin?: number;
    salaryMax?: number;
    salaryType?: string;
    sellerId?: string;
    visibilitySettings?: any;
    // Parts rental pricing
    rentalRate?: number;
    rentalPeriod?: string;
  };
  onViewDetails: () => void;
  onClose: () => void;
}

export const MobileMapPreviewCard: React.FC<MobileMapPreviewCardProps> = ({
  listing,
  onViewDetails,
  onClose
}) => {
  const { currency } = useCurrency();
  const { user } = useAuth();
  const { openUnifiedContact } = useUnifiedContact();

  // Privacy gating
  const { blurPrice: shouldHidePrice, blurLocation: shouldHideLocation, blurContact: shouldHideContact } =
    useEffectiveVisibility(listing.id, listing.visibilitySettings, listing.sellerId);
  const isOwner = user?.id === listing.sellerId;

  const requestField = useCallback((field: string) => {
    if (!user) {
      window.dispatchEvent(new CustomEvent('require-auth', {
        detail: { message: 'Please sign in to request this information' }
      }));
      return;
    }
    if (!listing.sellerId || listing.sellerId === user.id) return;
    openUnifiedContact({
      listingId: listing.id,
      listingTitle: listing.title,
      sellerId: listing.sellerId,
      assetType: listing.assetType || 'helicopter',
      hiddenFields: { price: shouldHidePrice, location: shouldHideLocation, contact: shouldHideContact },
      preselectedFields: [field],
    });
  }, [user, listing.id, listing.title, listing.sellerId, listing.assetType, shouldHidePrice, shouldHideLocation, shouldHideContact, openUnifiedContact]);

  // Get formatted price based on asset type
  const getFormattedPrice = () => {
    // Services - use service-specific formatter
    if (listing.assetType === 'services') {
      const result = formatServiceListingPrice(
        listing.baseRate,
        listing.pricingModel,
        listing.rateUnit,
        currency
      );
      return {
        text: result.formatted,
        displayType: result.displayType,
      };
    }
    
    // Jobs - use salary formatter
    if (listing.assetType === 'jobs') {
      const salary = formatJobSalary(
        listing.salaryMin,
        listing.salaryMax,
        listing.salaryType,
        'Competitive',
        currency
      );
      return {
        text: salary || 'Competitive',
        displayType: salary ? 'price' : 'none',
      };
    }
    
    // Parts with rental rate
    if (listing.assetType === 'parts' && listing.rentalRate) {
      const rentalText = formatListingPrice(listing.rentalRate, listing.listingType || 'parts_rent', currency);
      return {
        text: listing.rentalPeriod ? `${rentalText} / ${listing.rentalPeriod}` : rentalText,
        displayType: 'price',
      };
    }

    // All other asset types
    return {
      text: formatListingPrice(listing.price, listing.listingType || 'sale', currency),
      displayType: 'price',
    };
  };

  const priceDisplay = getFormattedPrice();

  return (
    <Card 
      className={cn(
        "absolute bottom-4 left-3 right-3 z-50",
        "flex gap-3 p-3",
        "glass-morphism-strong",
        "border border-border/50",
        "shadow-xl shadow-black/20",
        "rounded-2xl",
        "animate-in slide-in-from-bottom-4 duration-300"
      )}
    >
      {/* Close button */}
      <Button
        size="icon"
        variant="ghost"
        aria-label="Close preview"
        className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-background border shadow-md z-10"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <X className="h-4 w-4" />
      </Button>

      {/* Image - smart handling for job and service company logos */}
      <div className={cn(
        "relative w-[90px] h-[90px] flex-shrink-0 rounded-xl overflow-hidden",
        "bg-muted/50 shadow-inner"
      )}>
        {/* For job posts/contracts and service providers, use contain to prevent logo cropping */}
        {(listing.assetType === 'jobs' && (listing.listingType === 'job_post' || listing.listingType === 'job_contract')) ||
         (listing.assetType === 'services' && (listing.listingType === 'service_offer' || listing.listingType === 'capability_listing')) ? (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 p-2">
            <img
              src={listing.heroImage || listing.companyLogo || heroPlaceholder}
              alt={listing.companyName || listing.title}
              className="max-w-[85%] max-h-[85%] object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).src = heroPlaceholder;
              }}
            />
          </div>
        ) : (
          <OptimizedImage
            src={listing.heroImage || heroPlaceholder}
            alt={listing.title}
            className="w-full h-full object-cover"
            fallbackSrc={heroPlaceholder}
          />
        )}
        {/* Job Type Pin Icon */}
        {listing.assetType === 'jobs' && (
          <div className="absolute top-1.5 left-1.5 z-10">
            <div className={cn(
              "w-6 h-6 rounded-full bg-white/95 dark:bg-slate-800/95 shadow-md flex items-center justify-center",
              isJobOffering(listing.listingType) ? "ring-1 ring-emerald-400" : "ring-1 ring-amber-400"
            )}>
              <img src={getJobTypeMapPin(listing.listingType)} alt="" className="w-4 h-4" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>
          </div>
        )}
        {/* Service Type Pin and Category Badge */}
        {listing.assetType === 'services' && (
          <div className="absolute top-1.5 left-1.5 z-10 flex items-center gap-1">
            <div className={cn(
              "w-6 h-6 rounded-full bg-white/95 dark:bg-slate-800/95 shadow-md flex items-center justify-center",
              (listing.listingType === 'service_offer' || listing.listingType === 'capability_listing')
                ? "ring-1 ring-emerald-400" 
                : "ring-1 ring-amber-400"
            )}>
              <img src={getServiceTypeMapPin(listing.listingType)} alt="" className="w-4 h-4" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>
            {/* Show Category Badge instead of type badge */}
            <Badge 
              variant="secondary" 
              className={cn(
                "text-[8px] px-1.5 py-0.5 shadow-sm font-bold",
                listing.serviceCategory === 'mro' && "bg-blue-500/90 text-white border-0",
                listing.serviceCategory === 'camo' && "bg-purple-500/90 text-white border-0",
                listing.serviceCategory === 'part21' && "bg-orange-500/90 text-white border-0",
                listing.serviceCategory === 'training' && "bg-green-500/90 text-white border-0",
                listing.serviceCategory === 'operator' && "bg-sky-500/90 text-white border-0",
                listing.serviceCategory === 'engineering' && "bg-slate-500/90 text-white border-0",
                listing.serviceCategory === 'ground_ops' && "bg-amber-500/90 text-white border-0",
                listing.serviceCategory === 'fbo_heliport' && "bg-indigo-500/90 text-white border-0",
                listing.serviceCategory === 'management' && "bg-rose-500/90 text-white border-0",
                listing.serviceCategory === 'uas' && "bg-cyan-500/90 text-white border-0",
                !listing.serviceCategory && (
                  (listing.listingType === 'service_offer' || listing.listingType === 'capability_listing')
                    ? "bg-emerald-500/90 text-white border-0"
                    : "bg-amber-500/90 text-white border-0"
                )
              )}
            >
              {listing.serviceCategory 
                ? getServiceCategoryShortLabel(listing.serviceCategory)
                : (listing.listingType === 'service_offer' ? 'Provider' : 
                   listing.listingType === 'capability_listing' ? 'Capability' :
                   listing.listingType === 'rfq_request' ? 'RFQ' : 'Request')
              }
            </Badge>
          </div>
        )}
        {/* Listing type badge - offset when job/service pin is present */}
        {listing.assetType !== 'jobs' && listing.assetType !== 'services' && (
          <div className="absolute top-1.5 left-1.5">
            <ListingTypeBadge 
              listingType={listing.listingType || 'sale'} 
              className="text-[9px] px-1.5 py-0.5 shadow-sm" 
            />
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
        {/* Title + Location */}
        <div className="space-y-1">
          <h4 className="font-semibold text-sm line-clamp-2 leading-tight text-foreground">
            {listing.manufacturer && listing.model 
              ? `${listing.manufacturer} ${listing.model}`
              : listing.title}
          </h4>
          {shouldHideLocation ? (
            <BlurredPrivateData
              placeholderContent={
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="w-3 h-3 flex-shrink-0 text-primary/60" />
                  <span className="truncate">{listing.location || 'Hidden'}</span>
                </div>
              }
              fieldName="Location"
              size="sm"
              onRequestInfo={isOwner ? undefined : () => requestField('location')}
            />
          ) : listing.location ? (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="w-3 h-3 flex-shrink-0 text-primary/60" />
              <span className="truncate">{listing.location}</span>
            </div>
          ) : null}
        </div>
        
        {/* Price + CTA */}
        <div className="flex items-center justify-between gap-2 mt-2">
          {shouldHidePrice ? (
            <BlurredPrivateData
              placeholderContent={
                <div className="px-2.5 py-1 rounded-md shadow-sm bg-muted/30 border border-border/40">
                  <span className="text-[8px] text-muted-foreground block uppercase tracking-wide font-medium">Price</span>
                  <div className="text-sm font-bold text-foreground">$XXX,XXX</div>
                </div>
              }
              fieldName="Price"
              size="sm"
              onRequestInfo={isOwner ? undefined : () => requestField('price')}
            />
          ) : (
            <div className={cn(
              "px-2.5 py-1 rounded-md shadow-sm",
              listing.listingType === 'buy_request' || listing.listingType === 'rent_request'
                ? "bg-gradient-to-br from-[hsl(45,60%,97%)] to-[hsl(45,50%,93%)] dark:from-[hsl(45,40%,15%)] dark:to-[hsl(45,30%,10%)] border border-[hsl(45,70%,80%)]/60 dark:border-[hsl(45,50%,30%)]/40"
                : "bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-900/30 border border-slate-200/60 dark:border-slate-700/40"
            )}>
              {(listing.listingType === 'buy_request' || listing.listingType === 'rent_request') && (
                <span className="text-[8px] text-[hsl(45,55%,42%)] dark:text-[hsl(45,60%,60%)] block uppercase tracking-wide font-medium">Budget</span>
              )}
              <div className={cn(
                "text-sm font-bold",
                listing.listingType === 'buy_request' || listing.listingType === 'rent_request'
                  ? "text-[hsl(45,60%,32%)] dark:text-[hsl(45,70%,65%)]"
                  : "text-slate-800 dark:text-slate-200"
              )}>
                {priceDisplay.displayType === 'quote' ? (
                  <div className="flex items-center gap-1">
                    <FileText className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">Quote</span>
                  </div>
                ) : priceDisplay.displayType === 'contact' ? (
                  <span className="text-xs text-muted-foreground italic">Contact</span>
                ) : (
                  <span className="text-sm font-bold text-slate-800 dark:text-slate-200">{priceDisplay.text}</span>
                )}
              </div>
            </div>
          )}
          <Button 
            size="sm" 
            className="h-8 px-4 rounded-lg text-xs font-medium"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails();
            }}
          >
            View Details
          </Button>
        </div>
      </div>
    </Card>
  );
};
